import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import vendorService from "../services/vendor.service";
import { setMessage } from "./message";
export const fetchVendorList = createAsyncThunk(
    'vendor/fetchVendorList',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await vendorService.getVendorList(page, pagesize, search, ordering);
            return response.data; // Assuming response contains { data: { users, totalpage, etc. }}
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    }
);


export const fetchVendorDropdown = createAsyncThunk(
    'vendor/fetchVendorDropdown',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await vendorService.getVendorDropdown(page, pagesize, search, ordering);
            return response.data; // Assuming response contains { data: { users, totalpage, etc. }}
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    }
);
export const fetchArchiveVendorList = createAsyncThunk(
    'vendor/fetchArchiveVendorList',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await vendorService.getVendorArchiveList(page, pagesize, search, ordering);
            return response.data; // Assuming response contains { data: { users, totalpage, etc. }}
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    }
);
export const fetchVendorById = createAsyncThunk(
    'vendor/fetchById',
    async (id, { rejectWithValue }) => {
        try {
            const response = await vendorService.getVendorById(id);
            return response.data; // Return the profile data
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);
export const createVendor = createAsyncThunk(
    'vendor/createVendor',
    async ({ values }, thunkAPI) => {
        try {
            const response = await vendorService.createVendor(values);
            return response.data; // Assuming response contains the created category data
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);
export const editVendor = createAsyncThunk(
    'vendor/editVendor',
    async ({ id, values }, thunkAPI) => {
        try {
            const response = await vendorService.editVendor(id, values);
            return response.data; // Ensure this returns the updated category data
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const deleteVendor = createAsyncThunk(
    'vendor/deleteVendor',
    async (id, thunkAPI) => {
        try {
            await vendorService.deleteVendor(id); // Ensure `id` is used here
            return id; // Return the ID of the deleted group
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const unArchiveVendor = createAsyncThunk(
    'vendor/unArchiveVendor',
    async (id, thunkAPI) => {
        try {
            await vendorService.unArchiveVendor(id); // Ensure `id` is used here
            return id; // Return the ID of the deleted group
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    }
);


const initialState = {
    data: [],
    list: [],
    loading: false,
    totalpage: 0,
    currentpage: 1,
    totalRecord: 0,
    status: 'idle',
    error: null,
    hasMore: true,
};

const vendorSlice = createSlice({
    name: 'vendor',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        resetVendorList: (state) => {
            state.list = []; // This will clear the list
        },
        resetCurrentVendorPage: (state) => {
            state.currentpage = 1;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVendorList.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchVendorList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentpage;
                state.data = action.payload.results.data || [];  // Update to match your API response
                state.totalRecord = action.payload.count || 0; // Handle count (total records)
                state.totalPages = action.payload.totalPages || 0; // Handle totalPages
                state.currentpage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchVendorList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });

        builder
            .addCase(fetchVendorDropdown.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchVendorDropdown.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentpage;
                state.data = action.payload.results.data || [];  // Update to match your API response
                state.totalRecord = action.payload.count || 0; // Handle count (total records)
                state.totalPages = action.payload.totalPages || 0; // Handle totalPages
                state.currentpage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchVendorDropdown.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });
        builder
            .addCase(fetchArchiveVendorList.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchArchiveVendorList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentpage;
                state.data = action.payload.results.data || [];  // Update to match your API response
                state.totalRecord = action.payload.count || 0; // Handle count (total records)
                state.totalPages = action.payload.totalPages || 0; // Handle totalPages
                state.currentpage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchArchiveVendorList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });

        builder
            .addCase(fetchVendorById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchVendorById.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(fetchVendorById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(createVendor.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(createVendor.fulfilled, (state, action) => {
                state.status = 'succeeded';
            })
            .addCase(createVendor.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });
        builder
            .addCase(editVendor.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const index = state.data.findIndex(category => category.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
            })
            .addCase(editVendor.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(deleteVendor.pending, state => {
                state.status = 'loading';
            })
            .addCase(deleteVendor.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
                state.list = state.list.filter(group => group.id !== action.payload); // Remove the deleted group from list
            })
            .addCase(deleteVendor.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(unArchiveVendor.pending, state => {
                state.status = 'loading';
            })
            .addCase(unArchiveVendor.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
            })
            .addCase(unArchiveVendor.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });
    },
});

export const { resetVendorList, resetCurrentVendorPage } = vendorSlice.actions;
const { reducer } = vendorSlice;
export default reducer;
